import { Component, HostBinding, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AlertController, IonicModule, ToastController } from '@ionic/angular';
import { NavNyomtatvanyService } from '../nav-nyomtatvany.service';
import { NaploService } from '../../naplo/naplo.service';
import { BerService } from '../../ber/ber.service';

/**
 *  NAV-KAPCSOLAT (M2M) – a gépi beküldés hitelesítője, ügyfelenként.
 *
 *  A NAV két szinten hitelesít: a SZOFTVER (kliens azonosító + jelszó) és az ADÓZÓ (technikai
 *  felhasználó + jelszó, valamint egy két félből álló aláírókulcs). A kulcs második felét a NAV nem
 *  adja ki készen: a regisztrációkor kapott kóddal (nonce) kell kiváltani, majd a felhasználót
 *  aktiválni – ezt a „Kulcs kiváltása és aktiválás” gomb végzi el.
 *
 *  KÉT KÖRNYEZET, KÜLÖN REGISZTRÁCIÓVAL. A NAV tesztkörnyezete (m2m-dev.nav.gov.hu) saját
 *  hitelesítőt és teszt-adószámot ad; az éles az m2m.nav.gov.hu. Itt mindkettő eltárolható, és
 *  külön látszik, MELYIK van használatban: élesbe csak kifejezett átkapcsolás után megy bármi.
 *
 *  Az ügyfél összeköthető a másik modul ügyfelével (Bér ↔ Könyvelés): ilyenkor UGYANEZ a beállítás
 *  érvényes mindkét helyen – a lap ezt ki is írja.
 */
@Component({
  selector: 'app-nav-kapcsolat',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RouterModule],
  templateUrl: './nav-kapcsolat.component.html',
  /*  A BÉR közös stílusát NEM húzzuk be: az a terrakotta színt MINDEN oldalra ráállítaná, így a
   *  képernyő a Könyvelésben is piros lett. A modul színe a :host(.ber) osztályból jön. */
  styleUrls: ['../../naplo/naplo-shared.scss', './nav-kapcsolat.component.scss'],
})
export class NavKapcsolatComponent implements OnInit {
  @HostBinding('class.ber') get berMod() { return this.modul === 'BER'; }
  modul: 'NAPLO' | 'BER' = 'NAPLO';
  allapot: any = null;
  betolt = true;
  /** A szerkesztés alatt álló mezők környezetenként (a titkokat üresen hagyva a mentett marad). */
  urlap: Record<string, any> = { eles: {}, teszt: {} };
  fut: string | null = null;
  /*  A NAV TECHNIKAI FELHASZNÁLÓ (Online Számla) ugyanennek a CÉGNEK a hitelesítője, csak másik
   *  rendszerhez: ebből megy az EFO-lekérdezés, a NAV törzsadat és az adószám-ellenőrzés is.
   *  Eddig csak a Könyvelésből lehetett felvenni – ezért került ide, ahol a bérszámfejtő is eléri. */
  osa: any = null;
  /*  A technikai felhasználóból is van TESZT és ÉLES (a NAV tesztrendszere külön regisztráció).
   *  Az űrlapot környezetenként tartjuk – ahogy az M2M-nél is. */
  osaUrlap: Record<string, any> = { teszt: {}, eles: {} };

  constructor(private ny: NavNyomtatvanyService, private naplo: NaploService, private ber: BerService,
              private route: ActivatedRoute, private toast: ToastController, private alert: AlertController) {}

  get aktiv(): { ugyfelId: string | null; ugyfelNev: string | null } {
    return this.modul === 'BER' ? (this.ber.aktiv as any) : (this.naplo.aktiv as any);
  }

  async ngOnInit() {
    this.modul = this.route.snapshot.data['modul'] === 'BER' ? 'BER' : 'NAPLO';
    if (this.modul === 'BER') await this.ber.init(); else await this.naplo.init();
    await this.betoltes();
  }

  async betoltes() {
    this.betolt = true;
    try {
      if (!this.aktiv.ugyfelId) { this.allapot = null; return; }
      this.allapot = await this.ny.get(`/m2m/${this.modul}/${this.aktiv.ugyfelId}`);
      this.urlap = { eles: {}, teszt: {} };
      this.osa = await this.ny.get(`/osa/${this.modul}/${this.aktiv.ugyfelId}`).catch(() => null);
      this.osaUrlap = {
        teszt: { login: this.osa?.teszt?.login || '', adoszam: this.osa?.teszt?.adoszam || '' },
        eles: { login: this.osa?.eles?.login || '', adoszam: this.osa?.eles?.adoszam || (this.osa?.adoszam || '') },
      };
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni a NAV-kapcsolatot.'); }
    finally { this.betolt = false; }
  }

  readonly kornyezetek: ('teszt' | 'eles')[] = ['teszt', 'eles'];
  /** Vágólapra másolás (a kliens azonosítóhoz). Ha a böngésző nem engedi, a szöveg kijelölhető marad. */
  async masol(szoveg: string) {
    try { await navigator.clipboard.writeText(String(szoveg || '')); this.ok('Kimásolva.'); }
    catch { this.hiba('A böngésző nem engedte a másolást – jelöld ki a szöveget, és Ctrl+C.'); }
  }
  kornyezetCim(k: string) { return k === 'teszt' ? 'Tesztkörnyezet' : 'Éles környezet'; }

  async ment(kornyezet: 'eles' | 'teszt') {
    this.fut = 'ment-' + kornyezet;
    try {
      const r: any = await this.ny.put(`/m2m/${this.modul}/${this.aktiv.ugyfelId}`, { kornyezet, ...this.urlap[kornyezet] });
      this.ok(`${this.kornyezetCim(kornyezet)}: mentve${r[kornyezet]?.kesz ? ' – a hitelesítő teljes.' : ' – még hiányzik adat a beküldéshez.'}`);
      await this.betoltes();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
    finally { this.fut = null; }
  }

  /** Kapcsolatpróba: csak jegyet kér a NAV-tól – nem küld be és nem kérdez le adatot. */
  async proba(kornyezet: 'eles' | 'teszt') {
    this.fut = 'proba-' + kornyezet;
    try {
      const r: any = await this.ny.post(`/m2m/${this.modul}/${this.aktiv.ugyfelId}/proba`, { kornyezet });
      this.ok(`${this.kornyezetCim(kornyezet)}: ${r.uzenet}`);
    } catch (e: any) { this.hiba(e?.error?.error || 'A NAV nem fogadta el a hitelesítőt.'); }
    finally { this.fut = null; }
  }

  /** A kulcs második felének kiváltása a kapott kóddal, majd a felhasználó aktiválása. */
  async kulcs(kornyezet: 'eles' | 'teszt') {
    const nonce = (this.urlap[kornyezet]?.nonce || '').trim();
    this.fut = 'kulcs-' + kornyezet;
    try {
      const r: any = await this.ny.post(`/m2m/${this.modul}/${this.aktiv.ugyfelId}/kulcs`, { kornyezet, nonce });
      this.ok(r.aktivalas?.ok ? 'A kulcs második fele megjött, a felhasználó aktiválva.'
        : `A kulcs második fele megjött. Aktiválás: ${r.aktivalas?.uzenet || r.aktivalas?.kod || 'nem sikerült'}.`);
      await this.betoltes();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült kiváltani a kulcsot.'); }
    finally { this.fut = null; }
  }

  /** Környezetváltás. Élesre váltani csak megerősítéssel lehet – onnantól a beküldés valódi. */
  async valt(kornyezet: 'eles' | 'teszt') {
    if (kornyezet === 'eles') {
      const a = await this.alert.create({
        header: 'Váltás éles környezetre',
        message: 'Ezután a beküldés a NAV ÉLES rendszerébe megy, valódi jogkövetkezménnyel. Biztosan átváltod?',
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Átváltok', role: 'ok' }],
      });
      await a.present();
      if ((await a.onDidDismiss()).role !== 'ok') return;
    }
    this.fut = 'valt';
    try {
      await this.ny.post(`/m2m/${this.modul}/${this.aktiv.ugyfelId}/valt`, { kornyezet, eles: kornyezet === 'eles' });
      this.ok(kornyezet === 'teszt' ? 'Tesztkörnyezetre váltva – a beküldés nem megy élesbe.' : 'Éles környezetre váltva.');
      await this.betoltes();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült váltani.'); }
    finally { this.fut = null; }
  }

  /* ── NAV technikai felhasználó (Online Számla) – ugyanaz a rekord mindkét modulban ── */
  async osaMent(kornyezet: 'teszt' | 'eles') {
    this.fut = 'osa-ment-' + kornyezet;
    try {
      await this.ny.put(`/osa/${this.modul}/${this.aktiv.ugyfelId}`, { kornyezet, ...this.osaUrlap[kornyezet], kep_auto: this.osa?.kep_auto });
      this.ok(`${this.kornyezetCim(kornyezet)}: a technikai felhasználó mentve.`);
      await this.betoltes();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
    finally { this.fut = null; }
  }

  /** Váltás a teszt és az éles technikai felhasználó között – a szinkron ezután azzal megy. */
  async osaValt(kornyezet: 'teszt' | 'eles') {
    this.fut = 'osa-valt';
    try {
      await this.ny.post(`/osa/${this.modul}/${this.aktiv.ugyfelId}/valt`, { kornyezet });
      this.ok(kornyezet === 'teszt' ? 'A NAV tesztrendszerének technikai felhasználója lépett életbe.' : 'Az éles technikai felhasználó lépett életbe.');
      await this.betoltes();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült váltani.'); }
    finally { this.fut = null; }
  }

  /** A számlaképek automatikus letöltése az aktív hitelesítőre vonatkozik. */
  async osaKepAuto(ertek: boolean) {
    try { await this.ny.put(`/osa/${this.modul}/${this.aktiv.ugyfelId}`, { kep_auto: ertek, login: this.osa?.login, adoszam: this.osa?.adoszam }); await this.betoltes(); }
    catch { this.hiba('Nem sikerült menteni.'); }
  }

  async osaProba() {
    this.fut = 'osa-proba';
    try {
      const r: any = await this.ny.post(`/osa/${this.modul}/${this.aktiv.ugyfelId}/proba`, {});
      this.ok(r.uzenet || 'Sikeres kapcsolat.');
    } catch (e: any) { this.hiba(e?.error?.error || 'A NAV nem fogadta el a technikai felhasználót.'); }
    finally { this.fut = null; }
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 4200, color: 'danger' })).present(); }
}
